import React, { MouseEvent, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import logo from "../../assets/images/logo.png";
import BrushStroke from "../../assets/images/BrushStroke-white.png";
import styles from "./LoginScreen.module.css";
import { Margin, Visibility, VisibilityOff } from "@mui/icons-material";
import { styled } from "@mui/system";
import { CreateSessionRequest, loginRequest } from "../../Controller/request";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import Loader from "../Loader/loader";
import { getDivisionName } from "../Profile/division";

const CenteredContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: "5vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  // '@media (min-width: 1024px) and (max-width: 1400px)': {
  //   marginTop: '5vh',
  // },
  [theme.breakpoints.down("sm")]: {
    marginTop: "2vh",
  },
  [theme.breakpoints.between("sm", "md")]: {
    marginTop: "3.6vh",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ecedf2",
    padding: "0.2vh 1.2vh",
    "& fieldset": {
      borderColor: "#ecedf2",
    },
    "&:hover fieldset": {
      borderColor: "#8c8484",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#8c8484",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "1rem",
  },
  "& input:-webkit-autofill": {
    backgroundColor: "#ecedf2",
    transition: "background-color 5000s ease-in-out 0s",
  },
  "& input:-moz-autofill": {
    backgroundColor: "#ecedf2",
  },
  [theme.breakpoints.down("sm")]: {
    width: "36vh",
    marginTop: 6,
    "& .MuiOutlinedInput-root": {
      fontSize: "0.6rem",
      padding: "0.1vh 0.5vh",
    },
    "& .MuiInputBase-input": {
      padding: "1vh 1vh ",
      fontSize: "0.8rem",
    },
  },
  [theme.breakpoints.between("sm", "md")]: {
    width: "48vh",
    marginTop: 12,
    "& .MuiOutlinedInput-root": {
      fontSize: "1.2rem",
      padding: "0.4vh 0.8vh",
    },
    "& .MuiInputBase-input": {
      padding: "1.2vh 1.5vh ",
      fontSize: "1.2rem",
    },
  },
}));

const containerStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 9999,
};

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoginBtnDisabled, setIsLoginBtnDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) =>
    event.preventDefault();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoginBtnDisabled(true);
    setLoading(true);
    try {
      setError(null);
      const data = await loginRequest({ emp_n: employeeId, pass: password });
      sessionStorage.setItem("userDetails", JSON.stringify(data));
      sessionStorage.setItem("userId", data["emp_No"]);
      createUserSessionId(data);
    } catch (error) {
      setIsLoginBtnDisabled(false);
      setLoading(false);
      if (error instanceof AxiosError) {
        const errorData = error.response?.data;
        const message = errorData?.["data"]?.["message"];
        if (message === "Please contact Admin For Access") {
          setError("Invalid credentials. Try again or connect to Techsaarthi.");
          toast.error(
            "Invalid credentials. Try again or connect to Techsaarthi."
          );
        } else if (message) {
          setError(message);
          toast.error(message);
        } else {
          setError("Invalid credentials. Try again or connect to Techsaarthi.");
          toast.error(
            "Invalid credentials. Try again or connect to Techsaarthi."
          );
        }
      } else {
        setError("Invalid credentials. Try again or connect to Techsaarthi.");
        toast.error(
          "Invalid credentials. Try again or connect to Techsaarthi."
        );
      }
    }
  };

  const createUserSessionId = async (data: any) => {
    try {
      const sessionData = await CreateSessionRequest({
        emp_No: data["emp_No"].toString(),
        phone_number: data["Contact No"],
        division_code: data["Division"].toString(),
        division_name: getDivisionName(data["Division"]) || "HO User",
        location: data["Location"],
        name: data["Name"],
        email: data["Email"],
        role: data["Role"],
      });
      const responseBody = JSON.parse(sessionData.data.body);
      navigate("/alembic-ask-tara");
      setLoading(false);
      sessionStorage.setItem("sessionid", responseBody.sessionid);
    } catch (error) {
      setIsLoginBtnDisabled(false);
      setLoading(false);
      setError("Try again or connect to Techsaarthi.");
      toast.error("Try again or connect to Techsaarthi.");
    }
  };

  return (
    <Box className={styles.root}>
      {loading && <Loader />}
      <ToastContainer />
      <Box className={`${styles.section} ${styles.leftSection}`}>
        <Card className={styles.leftCard}>
          <CardContent>
            <Box className={styles.topSection}>
              <p className={styles.umangBigText}>Alembic Ask TARA</p>
              <p className={styles.fontSmall}>Alembic AI Assistant</p>
            </Box>
            <Box className={styles.middleSection}>
              <p className={styles.fontMedium}>Get your queries answered</p>
              <p className={styles.fontMedium}>24x7 with Alembic Ask TARA</p>
              {/* <img src={BrushStroke} alt="brush" className={styles.brush} /> */}
            </Box>
            <Box className={styles.bottomSection}>
              <p style={{ fontWeight: 600 }} className={styles.fontSmall}>
                LOGIN FROM
              </p>
              <p style={{ fontWeight: 600 }} className={styles.fontSmall}>
                S&D / MY ALEMBIC
              </p>
              <p style={{ fontWeight: 600 }} className={styles.fontSmall}>
                CREDENTIALS
              </p>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box className={`${styles.section} ${styles.rightSection}`}>
        <Card className={styles.rightCard} sx={{ boxShadow: "none" }}>
          <CardContent style={{ padding: 0 }}>
            <Box className={styles.alignCenter}>
              <img src={logo} alt="Alembic Logo" className={styles.logo} />
            </Box>
            <Box className={styles.rightSectionBoxTwo}>
              <p className={styles.fontSmall} style={{ fontWeight: 600 }}>
                Welcome to
              </p>
              <p className={styles.umangTextRight}>Alembic Ask TARA</p>
              <p className={styles.fontSmall} style={{ color: "#a8a5a5" }}>
                Please login to your account
              </p>
            </Box>
            <CenteredContainer>
              <form onSubmit={handleSubmit}>
                <StyledTextField
                  placeholder="Enter Employee ID"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  required
                />
                <StyledTextField
                  placeholder="Enter S&D / MY Alembic Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                />
                {error && (
                  <Box width="100%">
                    <Typography color="error" align="center" mt={1}>
                      {error}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <button
                    type="submit"
                    className={styles.loginButton}
                    disabled={isLoginBtnDisabled}
                  >
                    LOGIN
                  </button>
                </Box>
              </form>
            </CenteredContainer>
            <Box display="flex" justifyContent="center">
              <Divider
                sx={{
                  width: "60  %",
                  backgroundColor: "#ECEDF2",
                  height: "5px",
                  borderBottomWidth: "0",
                }}
              />
            </Box>
            <Box className={styles.rightSectionBottom}>
              <p className={styles.fontXtraSmall}>
                {" "}
                In case of any queries, connect{" "}
              </p>
              <a
                href="mailto:techsaarthi@alembic.co.in"
                className={`${styles.fontXtraSmall} ${styles.link}`}
              >
                {" "}
                techsaarthi@alembic.co.in
              </a>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginScreen;
